import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack,
} from "@mui/material";

import AssessmentMetaFields from "../../../shared/AssessmentMetaFields";

const errorCodeToKey = (code) => {
  if (code === "assessment_not_found") return "errors.assessmentNotFound";
  return "errors.updateFailed";
};

/**
 * Edit an existing assessment's name & notes. Seeds its fields from the given
 * assessment and reports changes back through `onSave`.
 */
function EditAssessmentDialog({
  open,
  assessment,
  onClose,
  onSave,
  isSaving,
  errorCode,
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");

  // Re-seed whenever a different assessment is opened for editing.
  useEffect(() => {
    if (open) {
      setName(assessment?.name || "");
      setNotes(assessment?.notes || "");
    }
  }, [open, assessment]);

  const initialName = assessment?.name || "";
  const initialNotes = assessment?.notes || "";
  const isDirty = name !== initialName || notes !== initialNotes;

  const handleSave = () => {
    if (!isDirty || isSaving) return;
    onSave({ name: name.trim(), notes: notes.trim() });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("risksMap.history.editTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <AssessmentMetaFields
            name={name}
            notes={notes}
            onNameChange={setName}
            onNotesChange={setNotes}
            disabled={isSaving}
          />
          {errorCode ? (
            <FormHelperText error>{t(errorCodeToKey(errorCode))}</FormHelperText>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving} color="inherit">
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isDirty || isSaving}
        >
          {isSaving ? t("common.loading") : t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditAssessmentDialog;
